/**
 * 旅行日志系统 - 首页
 * 按照设计稿重新实现页面布局
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTravelMemories } from '../contexts/TravelMemoriesContext';
import { travelApi } from '../services/travelMemoriesApi';
import './TravelMemoriesHome.css';

const TravelMemoriesHome = () => {
  const navigate = useNavigate();
  const { currentSpace, spaces, selectSpace, loading: spacesLoading, error: spacesError } = useTravelMemories();
  const [travels, setTravels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visitedProvinces, setVisitedProvinces] = useState([]);

  // 加载当前空间的旅行列表
  useEffect(() => {
    if (currentSpace) {
      loadTravels();
    }
  }, [currentSpace]);

  // 首页始终显示空间列表，不自动选择空间
  // 如果用户想查看某个空间的内容，需要先选择空间

  const loadTravels = async () => {
    if (!currentSpace) {
      console.log('【TravelMemoriesHome】没有选中空间，跳过加载');
      return;
    }

    try {
      setLoading(true);
      const spaceId = currentSpace.id || currentSpace._id;
      console.log('【TravelMemoriesHome】开始加载旅行列表，空间ID:', spaceId);
      const response = await travelApi.getTravels(spaceId);
      console.log('【TravelMemoriesHome】API响应:', response);
      
      if (response && response.success) {
        const travelsData = response.data || [];
        console.log('【TravelMemoriesHome】解析后的旅行数据:', travelsData);
        console.log('【TravelMemoriesHome】旅行数量:', travelsData.length);
        setTravels(travelsData);

        // 提取访问过的省份（根据地点信息）
        const provinces = extractProvinces(travelsData);
        setVisitedProvinces(provinces);
      } else {
        console.warn('【TravelMemoriesHome】API响应格式不正确:', response);
      }
    } catch (error) {
      console.error('【TravelMemoriesHome】加载旅行列表失败:', error);
      console.error('【TravelMemoriesHome】错误详情:', {
        message: error.message,
        response: error.response,
        responseData: error.response?.data
      });
    } finally {
      setLoading(false);
    }
  };

  // 从旅行数据中提取省份信息
  const extractProvinces = (travelsData) => {
    // 简单的省份映射（可以根据实际地点数据优化）
    const provinceMap = {
      '四川': 'sichuan',
      '成都': 'sichuan',
      '都江堰': 'sichuan',
      '青城山': 'sichuan',
      '甘肃': 'gansu',
      '张掖': 'gansu',
      '嘉峪关': 'gansu',
      '青海': 'qinghai',
      '西宁': 'qinghai',
      '青海湖': 'qinghai',
      '茶卡盐湖': 'qinghai',
      '广西': 'guangxi',
      '崇左': 'guangxi',
      '梧州': 'guangxi',
      '德天瀑布': 'guangxi',
      '明仕田园': 'guangxi',
      '敦煌': 'gansu',
      '瓜州': 'gansu',
      '大柴旦': 'qinghai',
      '翡翠湖': 'qinghai',
      '水上雅丹': 'qinghai',
      '茶卡': 'qinghai',
      '门源': 'qinghai',
      '祁连': 'qinghai'
    };

    const provinces = new Set();
    travelsData.forEach(travel => {
      if (travel.locations && Array.isArray(travel.locations)) {
        travel.locations.forEach(location => {
          const province = provinceMap[location];
          if (province) {
            provinces.add(province);
          }
        });
      }
    });

    return Array.from(provinces);
  };

  // 如果没有空间，显示空间选择提示
  if (spacesLoading) {
    return (
      <div className="travel-memories-loading">
        <div>加载中...</div>
      </div>
    );
  }

  // 如果有错误，显示错误信息
  if (spacesError && spaces.length === 0) {
    return (
      <div className="travel-memories-empty">
        <div className="empty-content">
          <h2>加载失败</h2>
          <p>{spacesError}</p>
          <button 
            className="btn-primary"
            onClick={() => window.location.reload()}
          >
            刷新页面
          </button>
        </div>
      </div>
    );
  }

  // 首页始终显示空间列表，让用户选择要查看的空间
  // 只有当用户明确选择了一个空间后，才显示该空间的旅行内容
  if (!currentSpace) {
    // 分离已加入和未加入的空间（系统管理员视图）
    const userStr = localStorage.getItem('user');
    let isAdmin = false;
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        isAdmin = user.isAdmin === true || user.isAdmin === 'true';
      } catch (e) {
        console.error('解析用户信息失败:', e);
      }
    }
    
    const joinedSpaces = spaces.filter(s => s.role);
    const unjoinedSpaces = isAdmin ? spaces.filter(s => !s.role) : [];

    return (
      <div className="travel-memories-home">
        {/* Header - 顶部标题区域 */}
        <header className="tm-header">
          <div className="tm-header-content">
            <h1 className="tm-title">旅行空间</h1>
          </div>
        </header>

        {/* 空间列表 */}
        <section className="tm-spaces-list-section">
          <div className="tm-detail-container">
            <h2 className="tm-section-title">{isAdmin ? '所有空间' : '我的空间'}</h2>
            <p className="tm-section-subtitle">
              {isAdmin ? '系统管理员可以查看和管理所有空间' : '选择一个空间开始记录旅行日志'}
            </p>

            {/* 已加入的空间 */}
            {joinedSpaces.length > 0 && (
              <div className="tm-spaces-grid-home">
                {joinedSpaces.map((space) => (
                  <div
                    key={space.id || space._id}
                    className="tm-space-card-home"
                    onClick={() => {
                      selectSpace(space);
                      // 选择空间后，useEffect会自动加载该空间的旅行数据
                    }}
                  >
                    {space.coverImage && (
                      <div className="tm-space-cover-home">
                        <img src={space.coverImage} alt={space.name} />
                      </div>
                    )}
                    <div className="tm-space-content-home">
                      <h3 className="tm-space-name-home">{space.name}</h3>
                      {space.description && (
                        <p className="tm-space-description-home">{space.description}</p>
                      )}
                      <div className="tm-space-meta-home">
                        <span className={`tm-space-role-home tm-role-${space.role}`}>
                          {space.role === 'owner' ? '所有者' : space.role === 'editor' ? '编辑者' : '查看者'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 未加入的空间（仅系统管理员可见） */}
            {isAdmin && unjoinedSpaces.length > 0 && (
              <div className="tm-spaces-section-home">
                <h3 className="tm-section-subtitle">其他空间</h3>
                <div className="tm-spaces-grid-home">
                  {unjoinedSpaces.map((space) => (
                    <div
                      key={space.id || space._id}
                      className="tm-space-card-home tm-space-card-unjoined"
                      onClick={() => {
                        alert('系统管理员需要先加入空间才能访问');
                      }}
                    >
                      {space.coverImage && (
                        <div className="tm-space-cover-home">
                          <img src={space.coverImage} alt={space.name} />
                        </div>
                      )}
                      <div className="tm-space-content-home">
                        <h3 className="tm-space-name-home">{space.name}</h3>
                        {space.description && (
                          <p className="tm-space-description-home">{space.description}</p>
                        )}
                        <div className="tm-space-meta-home">
                          <span className="tm-space-role-home tm-role-unjoined">
                            未加入
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 空状态 */}
            {spaces.length === 0 && (
              <div className="tm-empty-state-home">
                <h2>还没有空间</h2>
                <p>创建一个空间开始记录你的旅行日志吧</p>
                <button 
                  className="btn-primary"
                  onClick={() => navigate('/travel-memories/spaces/new')}
                >
                  创建空间
                </button>
              </div>
            )}

            {/* 创建新空间卡片 */}
            {spaces.length > 0 && (
              <div className="tm-spaces-section-home">
                <div className="tm-spaces-grid-home">
                  <div
                    className="tm-space-card-home tm-space-card-new"
                    onClick={() => navigate('/travel-memories/spaces/new')}
                  >
                    <div className="tm-space-card-new-content">
                      <div className="tm-space-card-new-icon">+</div>
                      <h3>创建新空间</h3>
                      <p>开始记录新的旅行日志</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }

  // 获取前3个旅行记录用于摘要显示
  const summaryTravels = travels.slice(0, 3);
  // 获取所有旅行记录用于底部详情卡片
  const detailTravels = travels.slice(0, 3);

  return (
    <div className="travel-memories-home">
      {/* Header - 顶部标题区域 */}
      <header className="tm-header">
        <div className="tm-header-content">
          <h1 className="tm-title">旅行空间</h1>
          {currentSpace && (
            <div className="tm-space-info">
              <span className="tm-space-name">{currentSpace.name}</span>
              <button 
                className="tm-switch-space-btn"
                onClick={() => {
                  selectSpace(null);
                  localStorage.removeItem('travelMemories_currentSpace');
                }}
              >
                切换空间
              </button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content - 中间两栏布局 */}
      {currentSpace && travels.length > 0 && (
        <section className="tm-main-content">
          {/* 左侧：地图展示 */}
          <div className="tm-map-section">
            <h2 className="tm-section-title">我们的足迹</h2>
            <p className="tm-map-hint">滚轮缩放地图，点击彩色省份查看详情</p>
            <div className="tm-map-container">
              <ChinaMap visitedProvinces={visitedProvinces} />
            </div>
          </div>

          {/* 右侧：旅行列表摘要 */}
          <div className="tm-summary-section">
            <h2 className="tm-section-title">旅行列表摘要</h2>
            <div className="tm-summary-list">
              {summaryTravels.map((travel) => (
                <div 
                  key={travel.id || travel._id} 
                  className="tm-summary-card"
                  onClick={() => navigate(`/travel-memories/spaces/${currentSpace._id || currentSpace.id}/travels/${travel.id || travel._id}`)}
                >
                  <h3 className="tm-summary-title">{travel.title}</h3>
                  {travel.subtitle && (
                    <p className="tm-summary-theme">{travel.subtitle}</p>
                  )}
                  <div className="tm-summary-meta">
                    <div className="tm-summary-item">
                      <span className="tm-summary-icon">📅</span>
                      <span>{travel.startDate}</span>
                    </div>
                    {travel.locations && travel.locations.length > 0 && (
                      <div className="tm-summary-item">
                        <span className="tm-summary-icon">📍</span>
                        <span>{travel.locations.join(' · ')}</span>
                      </div>
                    )}
                    <div className="tm-summary-item">
                      <span className="tm-summary-icon">⭐</span>
                      <span>精彩瞬间</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom Section - 底部旅行详情卡片 */}
      {currentSpace && detailTravels.length > 0 && (
        <section className="tm-detail-section">
          <div className="tm-detail-grid">
            {detailTravels.map((travel) => (
              <div
                key={travel.id || travel._id}
                className="tm-detail-card"
                onClick={() => navigate(`/travel-memories/spaces/${currentSpace._id || currentSpace.id}/travels/${travel.id || travel._id}`)}
              >
                {travel.coverImage ? (
                  <div className="tm-detail-cover">
                    <img src={travel.coverImage} alt={travel.title} />
                  </div>
                ) : (
                  <div className="tm-detail-cover tm-detail-cover-placeholder">
                    <div className="tm-placeholder-content">
                      <span className="tm-placeholder-icon">✈️</span>
                      <span className="tm-placeholder-text">{travel.title}</span>
                    </div>
                  </div>
                )}
                <div className="tm-detail-content">
                  <h3 className="tm-detail-title">{travel.title}</h3>
                  <p className="tm-detail-date">
                    {travel.startDate} - {travel.endDate}
                  </p>
                  {travel.locations && travel.locations.length > 0 && (
                    <div className="tm-detail-locations">
                      {travel.locations.map((location, index) => (
                        <span key={index} className="tm-location-tag">
                          {location}
                        </span>
                      ))}
                    </div>
                  )}
                  {travel.description && (
                    <p className="tm-detail-description">{travel.description}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Empty State */}
      {currentSpace && !loading && travels.length === 0 && (
        <div className="tm-empty-travels">
          <p>还没有旅行记录</p>
          {(currentSpace?.role === 'owner' || currentSpace?.role === 'editor') && (
            <button 
              className="btn-primary"
              onClick={() => navigate(`/travel-memories/spaces/${currentSpace.id || currentSpace._id}/travels/new`)}
            >
              创建第一个旅行
            </button>
          )}
        </div>
      )}
    </div>
  );
};

// 简单的中国地图组件
const ChinaMap = ({ visitedProvinces }) => {
  // 省份颜色映射
  const getProvinceColor = (province) => {
    if (visitedProvinces.includes(province)) {
      // 根据省份返回不同颜色
      if (province === 'sichuan') return '#FF6B6B'; // 四川 - 红色
      if (province === 'gansu') return '#FFB6A3'; // 甘肃 - 橙红色
      if (province === 'qinghai') return '#FFB6A3'; // 青海 - 橙红色
      if (province === 'guangxi') return '#B8E6D5'; // 广西 - 绿色
      return '#FFB6A3'; // 默认颜色
    }
    return '#E8E8E8'; // 未访问的省份 - 灰色
  };

  return (
    <div className="tm-china-map">
      <svg viewBox="0 0 1000 800" className="tm-map-svg">
        {/* 简化的中国地图轮廓 */}
        {/* 四川 */}
        <path
          d="M 300 400 L 350 380 L 380 400 L 370 450 L 340 480 L 300 470 L 280 430 Z"
          fill={getProvinceColor('sichuan')}
          stroke="#fff"
          strokeWidth="2"
          className="tm-province-path"
          data-province="sichuan"
        />
        
        {/* 甘肃 */}
        <path
          d="M 250 200 L 350 180 L 380 220 L 370 280 L 340 320 L 300 300 L 280 250 Z"
          fill={getProvinceColor('gansu')}
          stroke="#fff"
          strokeWidth="2"
          className="tm-province-path"
          data-province="gansu"
        />
        
        {/* 青海 */}
        <path
          d="M 200 250 L 280 230 L 300 280 L 280 350 L 240 380 L 200 350 L 180 300 Z"
          fill={getProvinceColor('qinghai')}
          stroke="#fff"
          strokeWidth="2"
          className="tm-province-path"
          data-province="qinghai"
        />
        
        {/* 广西 */}
        <path
          d="M 450 550 L 550 530 L 580 570 L 570 620 L 540 650 L 500 640 L 480 600 Z"
          fill={getProvinceColor('guangxi')}
          stroke="#fff"
          strokeWidth="2"
          className="tm-province-path"
          data-province="guangxi"
        />
        
        {/* 其他省份的简化轮廓 */}
        <rect x="100" y="100" width="600" height="600" fill="none" stroke="#ddd" strokeWidth="1" strokeDasharray="5,5" />
        
        {/* 提示文字 */}
        {visitedProvinces.length > 0 && (
          <text x="500" y="750" textAnchor="middle" fontSize="16" fill="#666" fontWeight="600">
            已访问 {visitedProvinces.length} 个省份
          </text>
        )}
      </svg>
    </div>
  );
};

export default TravelMemoriesHome;
